import type { LiveMedia, LiveMediaEndDetail } from "./liveTalkController";
import {
  liveUpdateEvent,
  readLiveDelegationEvent,
  readLiveTranscriptEvent,
  type LiveClientDelegation,
  type LiveMediaCallbacks,
  type LiveMediaUpdate,
  type LiveTranscriptFragment,
} from "./liveDelegationProtocol";

/** GPT-Live WebRTC uses media tracks plus oai-events; HTTP starts the session. */
export function createLiveMedia(
  onReady: () => void,
  onEnd: (error?: string, detail?: LiveMediaEndDetail) => void,
  callbacks: LiveMediaCallbacks = {},
): LiveMedia & { sendUpdate: (update: LiveMediaUpdate) => void; released: () => boolean } {
  const peer = new RTCPeerConnection();
  const channel = peer.createDataChannel("oai-events");
  const audio = document.createElement("audio");
  audio.autoplay = true;
  audio.hidden = true;
  document.body.appendChild(audio);
  let stream: MediaStream | null = null;
  let closed = false;
  let providerSessionId: string | null = null;
  let observedSessionId: string | null = null;
  let verified = false;
  let ended = false;
  const delegationIds = new Set<string>();
  const sentAppendIds = new Set<string>();
  const receivedTracks = new Set<MediaStreamTrack>();
  type PendingEvent = { transcript: LiveTranscriptFragment } | { delegation: LiveClientDelegation };
  const pending: PendingEvent[] = [];
  let pendingBytes = 0;
  const fail = (error?: string) => {
    ended = true;
    pending.length = 0;
    pendingBytes = 0;
    sentAppendIds.clear();
    onEnd(error);
  };
  const deliver = (event: PendingEvent) => {
    if (closed || ended) return;
    if ("transcript" in event) callbacks.onTranscript?.(event.transcript);
    else {
      if (!delegationIds.has(event.delegation.id) && delegationIds.size >= 64) {
        fail("Live received too many delegation requests for this bounded call.");
        return;
      }
      delegationIds.add(event.delegation.id);
      callbacks.onDelegation?.(event.delegation);
    }
  };
  const receive = (event: PendingEvent) => {
    if (verified) {
      deliver(event);
      return;
    }
    const bytes = new TextEncoder().encode(JSON.stringify(event)).length;
    if (pending.length >= 128 || pendingBytes + bytes > 32_000) {
      fail("Live events exceeded the limit before provider identity was verified.");
      return;
    }
    pending.push(event);
    pendingBytes += bytes;
  };
  let iceReject: ((reason: Error) => void) | undefined;
  let iceTimer: ReturnType<typeof setTimeout> | undefined;
  const verifyReady = () => {
    if (!providerSessionId || !observedSessionId || closed || ended) return;
    if (providerSessionId !== observedSessionId) {
      fail("Live provider identity did not match this call.");
      return;
    }
    if (verified) return;
    verified = true;
    onReady();
    const buffered = pending.splice(0);
    pendingBytes = 0;
    for (const event of buffered) deliver(event);
  };
  channel.onmessage = ({ data }) => {
    if (closed || ended || typeof data !== "string") return;
    try {
      const event = JSON.parse(data);
      if (event.type === "session.started") {
        observedSessionId = event.session?.id ?? null;
        verifyReady();
      } else if (event.type === "session.closed") fail();
      else if (event.type === "error") {
        const rejection = event.error;
        const closingAppend =
          rejection?.type === "invalid_request_error" &&
          rejection.code === null &&
          rejection.param === null &&
          rejection.message === "The session is closing and cannot accept new client events." &&
          typeof rejection.client_event_id === "string" &&
          sentAppendIds.has(rejection.client_event_id);
        // This rejects an optional update during shutdown, not the call's
        // final usage. The trusted observer still retains the provider error.
        fail(closingAppend ? undefined : "The Live audio session reported an error.");
      } else {
        const transcript = readLiveTranscriptEvent(event);
        const delegation = readLiveDelegationEvent(event);
        if (transcript) receive({ transcript });
        else if (delegation) receive({ delegation });
      }
      // Only the application may turn a delegation snapshot into ordinary Send.
    } catch {
      fail("Live returned an unreadable session event.");
    }
  };
  peer.ontrack = (event) => {
    if (closed) {
      event.track.stop();
      return;
    }
    receivedTracks.add(event.track);
    audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
    void audio.play().catch(() => {
      if (!closed) onEnd("Audio playback was blocked. End and start Live again to allow playback.");
    });
  };
  peer.onconnectionstatechange = () => {
    if (!closed && (peer.connectionState === "failed" || peer.connectionState === "disconnected"))
      onEnd("The Live audio connection was interrupted.", { reason: "connection-lost" });
  };
  const close = () => {
    if (closed) return;
    closed = true;
    pending.length = 0;
    pendingBytes = 0;
    delegationIds.clear();
    sentAppendIds.clear();
    stream?.getTracks().forEach((track) => track.stop());
    for (const receiver of peer.getReceivers())
      if (receiver.track) receivedTracks.add(receiver.track);
    for (const track of receivedTracks) track.stop();
    audio.pause();
    audio.srcObject = null;
    audio.remove();
    clearTimeout(iceTimer);
    iceReject?.(new Error("Live ended before audio connected."));
    peer.onicegatheringstatechange = null;
    peer.ontrack = null;
    peer.onconnectionstatechange = null;
    channel.onmessage = null;
    if (channel.readyState === "open") {
      try {
        channel.send(JSON.stringify({ type: "session.close" }));
      } catch {
        /* Host stop reconciles. */
      }
    }
    // The trusted host observer owns final usage; browser devices never wait on it.
    channel.close();
    peer.close();
  };
  /** Device release is observed, never assumed: every track ended and playback detached. */
  const released = () =>
    closed &&
    (stream?.getTracks() ?? []).every((track) => track.readyState === "ended") &&
    [...receivedTracks].every((track) => track.readyState === "ended") &&
    !audio.isConnected &&
    audio.srcObject === null;
  return {
    async offer() {
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (closed) {
        acquired.getTracks().forEach((track) => track.stop());
        throw new Error("Live ended before microphone capture.");
      }
      stream = acquired;
      stream.getTracks().forEach((track) => peer.addTrack(track, stream!));
      await peer.setLocalDescription(await peer.createOffer());
      if (closed) throw new Error("Live ended before audio connected.");
      if (peer.iceGatheringState !== "complete")
        await new Promise<void>((resolve, reject) => {
          iceReject = reject;
          iceTimer = setTimeout(
            () => reject(new Error("Live audio connection preparation timed out.")),
            10_000,
          );
          peer.onicegatheringstatechange = () => {
            if (peer.iceGatheringState !== "complete") return;
            clearTimeout(iceTimer);
            iceReject = undefined;
            peer.onicegatheringstatechange = null;
            resolve();
          };
        });
      return peer.localDescription?.sdp ?? "";
    },
    async answer(sdp, sessionId) {
      if (closed) return;
      providerSessionId = sessionId;
      await peer.setRemoteDescription({ type: "answer", sdp });
      verifyReady();
    },
    sendUpdate(update) {
      if (
        closed ||
        ended ||
        !verified ||
        providerSessionId !== observedSessionId ||
        channel.readyState !== "open"
      )
        throw new Error("Live updates require this call's verified open connection.");
      // A null delegation answers no delegation of this session (the guide
      // permits it); a named one must have been observed on this connection.
      if (update.delegationId !== null && !delegationIds.has(update.delegationId))
        throw new Error("Live updates require a client delegation observed on this call.");
      channel.send(JSON.stringify(liveUpdateEvent(update)));
      sentAppendIds.add(update.eventId);
      // Keep correlation local and bounded; an evicted ID takes the ordinary
      // error path rather than suppressing an unidentifiable rejection.
      if (sentAppendIds.size > 4096) sentAppendIds.delete(sentAppendIds.values().next().value!);
    },
    close,
    released,
  };
}
